import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  CircularProgress,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
  Alert,
  Tooltip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Label as TagIcon,
  Folder as GroupIcon,
  Info as InfoIcon,
  Reorder as ReorderIcon,
  AddCircle as AddCircleIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import type { UnifiedTag, CreateTagRequest, UpdateTagRequest } from '../../services';
import type { TagScope } from '../../types/content.types';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';

const TAG_COLOR_PALETTE = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
  '#ff5722', '#795548', '#9e9e9e', '#607d8b',
];
const randomTagColor = () => TAG_COLOR_PALETTE[Math.floor(Math.random() * TAG_COLOR_PALETTE.length)];

// Type definitions
interface RestTagGroup {
  id: string;
  name: string;
  allow_multiple?: boolean;
  color_scheme?: string;
}

// Type aliases for compatibility
type TagGroup = RestTagGroup & {
  description?: string;
  is_ordered?: boolean;
  tags?: Array<{
    id: string;
    name: string;
    orderInGroup?: number;
    [key: string]: any;
  }>;
};
type Tag = UnifiedTag & {
  color?: string;
  [key: string]: any;
};
type SoloTag = UnifiedTag; // Solo tags are now unified tags with no group

interface TagManagerProps {
  teamId: string;
  organizationId: string;
  userRole?: string | 'TeamAdmin' | 'TeamManager' | 'TeamMember' | 'TeamViewer';
}

interface DragData {
  type: 'tag' | 'group';
  id: string;
  sourceGroupId?: string;
  originalIndex?: number; // For reordering within groups
}

const TagManager: React.FC<TagManagerProps> = ({
  teamId,
  organizationId,
  userRole,
}) => {
  // Debug: Log the actual userRole value and props
  console.log('TagManager props:', {
    teamId,
    organizationId,
    userRole,
    teamIdType: typeof teamId,
    organizationIdType: typeof organizationId
  });
  
  // Access control - only TeamAdmin and TeamManager can manage tags
  // TEMPORARY: Allow all access for testing until role system is properly connected
  const canManageTags = true; // TODO: Implement proper role checking when backend is ready

  // State management
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  const [soloTags, setSoloTags] = useState<SoloTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState<DragData | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<number | null>(null);

  // Dialog states
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [showCreateTagDialog, setShowCreateTagDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<{ type: 'tag' | 'group'; item: any } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    severity: 'warning' | 'error';
    onConfirm: () => void;
  }>({ open: false, title: '', content: '', severity: 'warning', onConfirm: () => {} });

  // Form states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupIsOrdered, setNewGroupIsOrdered] = useState(false);
  const [newGroupAllowMultiple, setNewGroupAllowMultiple] = useState(true);
  const [newGroupColor, setNewGroupColor] = useState(randomTagColor);
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [newTagColor, setNewTagColor] = useState(randomTagColor);
  const [newTagTargetGroupId, setNewTagTargetGroupId] = useState<string>('solo');

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const colorPalette = TAG_COLOR_PALETTE;

  // Load tag groups and solo tags using unified tag system
  useEffect(() => {
    const loadTags = async () => {
      // Don't make API calls if organizationId is not available
      if (!organizationId) {
        console.log('TagManager: organizationId not available, skipping load');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        console.log('TagManager: Loading unified tags for organization:', organizationId, 'team:', teamId);
        
        // Use the proper API endpoints - let errors surface so we know what's broken
        const [tagGroupsResponse, allTagsResponse] = await Promise.all([
          // Get tag groups with their tags
          tagService.getTagGroupsWithTags({ organization_id: organizationId, team_id: teamId }),
          // Get all team tags (we'll filter out grouped ones client-side)
          tagService.listUnifiedTags({
            scope: 'team',  // Use correct scope parameter (matches TagScope.TEAM proto enum)
            organization_id: organizationId,
            team_id: teamId,
            limit: 1000,  // Increased limit to show all tags
            sort_by: 'created',  // Sort by creation date
            sort_order: 'desc'  // Newest first
          })
        ]);
        
        console.log('TagManager: Tag groups with tags response:', tagGroupsResponse);
        console.log('TagManager: All tags response:', allTagsResponse);
        
        // The service now properly extracts the data, returning arrays directly
        const groupsData = Array.isArray(tagGroupsResponse) ? tagGroupsResponse : [];
        const allTagsData = Array.isArray(allTagsResponse) ? allTagsResponse : [];
        
        console.log('TagManager: Processing', groupsData.length, 'tag groups from API');
        
        // Convert unified tags to RestTag format for groups and use array-based ordering
        const processedGroups = groupsData.map(groupData => {
          // The API returns flat tag groups with embedded tags array
          const group = groupData;

          // Map both camelCase and snake_case fields from API for maximum compatibility
          // Check both field naming conventions since API might return either format
          const rawIsOrdered = group.is_ordered ?? (group as any).isOrdered ?? false;
          const rawAllowMultiple = group.allow_multiple ?? (group as any).allowMultiple ?? (group as any).allows_multiple ?? true;
          const rawColorScheme = group.color_scheme ?? (group as any).colorScheme ?? group.color ?? '#666666';

          console.log(`TagManager: Normalizing group "${group.name}":`, {
            raw_is_ordered: group.is_ordered,
            raw_isOrdered: (group as any).isOrdered,
            normalized_is_ordered: rawIsOrdered,
            raw_allow_multiple: group.allow_multiple,
            raw_allowMultiple: (group as any).allowMultiple,
            raw_allows_multiple: (group as any).allows_multiple,
            normalized_allow_multiple: rawAllowMultiple,
            raw_color_scheme: group.color_scheme,
            raw_colorScheme: (group as any).colorScheme,
            normalized_color_scheme: rawColorScheme
          });

          // Map camelCase fields from API to snake_case fields expected by component
          const normalizedGroup = {
            ...group,
            is_ordered: rawIsOrdered,
            allow_multiple: rawAllowMultiple,
            color_scheme: rawColorScheme,
            organization_id: group.organization_id,
            team_id: group.team_id,
            created_at: group.created_at,
            updated_at: group.updated_at
          };
          
          const tags = (groupData.tags || []).map(unifiedTag => ({
            id: unifiedTag.id,
            name: unifiedTag.name,
            description: unifiedTag.description || '',
            color: unifiedTag.color || '#666666',
            order_in_group: unifiedTag.order ?? 0,
            tag_group_id: unifiedTag.tag_group_id
          }));

          // Sort tags according to the tag_order array if the group is ordered
          let sortedTags = tags;
          if (normalizedGroup.is_ordered) {
            // For ordered groups without order array, use order_in_group
            sortedTags = [...tags].sort((a, b) => (a.order_in_group || 0) - (b.order_in_group || 0));
          } else {
            // For unordered groups, sort by name
            sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name));
          }

          console.log(`TagManager: Processed group "${group.name}" with ${sortedTags.length} tags`);

          return {
            ...normalizedGroup,
            tags: sortedTags
          };
        });
        
        console.log('TagManager: Found', processedGroups.length, 'tag groups');
        console.log('TagManager: Setting tagGroups state with:', processedGroups);
        setTagGroups(processedGroups);
        
        // Process solo tags (individual tags not part of any group)
        console.log('TagManager: All tags from API:', allTagsData.length, 'tags');
        if (allTagsData.length > 0) {
          console.log('TagManager: Sample tag structure:', allTagsData[0]);
          console.log('TagManager: Sample tag keys:', Object.keys(allTagsData[0]));
        }
        
        // Get all tag IDs that are already in groups to exclude them from solo tags
        const tagIdsInGroups = new Set();
        processedGroups.forEach(group => {
          // Note: RestTagGroup no longer has tags property directly
          const groupTags = (group as any).tags || [];
          groupTags.forEach((tag: any) => {
            tagIdsInGroups.add(tag.id);
          });
        });
        console.log('TagManager: Tag IDs in groups:', Array.from(tagIdsInGroups));
        
        // Filter to get solo tags - tags that are:
        // 1. Not in any group
        // 2. Belong to this team (scope === 'team')
        // 3. Have is_solo flag set to true (indicates they're individual tags)
        const soloTags = allTagsData.filter(tag => {
          const isNotInGroup = !tagIdsInGroups.has(tag.id);
          // Check if it's a team tag (not personal) and is marked as solo
          // Use only documented UnifiedTag properties
          const hasTeamScope = tag.scope === 'team';
          const belongsToTeam = tag.team_id === teamId || (tag as any).owner_id === teamId;
          const isSolo = tag.is_solo === true;
          
          console.log(`TagManager: Filtering tag "${tag.name}":`, {
            isNotInGroup,
            hasTeamScope,
            belongsToTeam,
            isSolo,
            scope: tag.scope,
            team_id: tag.team_id,
            owner_id: (tag as any).owner_id,
            is_solo: tag.is_solo,
            tag_group_id: tag.tag_group_id
          });
          
          // Solo tags should be team tags that are not in groups 
          // For now, ignore is_solo flag and owner_id check since API might not be setting them correctly
          return isNotInGroup && hasTeamScope;
        });
        
        console.log('TagManager: Found', allTagsData.length, 'total tags,', soloTags.length, 'solo team tags after filtering out grouped tags and personal tags');
        
        setSoloTags(soloTags);
        console.log('TagManager: Set', processedGroups.length, 'tag groups and', soloTags.length, 'solo tags');
      } catch (error) {
        console.error('TagManager: Error loading unified tags:', error);
        console.error('TagManager: Error details:', {
          organizationId,
          teamId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined
        });
        setSnackbar({
          open: true,
          message: 'Failed to load tags',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadTags();
  }, [organizationId, teamId]);

  // Access control check - temporarily disabled for testing
  if (!canManageTags) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to manage tags. Only Team Admins and Team Managers can access this feature.
        </Alert>
      </Box>
    );
  }

  // Simplified reordering with up/down buttons
  const handleMoveTagUp = async (groupId: string, tagId: string, currentIndex: number) => {
    if (currentIndex === 0) return; // Already at top

    const group = tagGroups.find(g => g.id === groupId);
    if (!group || !group.tags) return;

    // Create new order by swapping with previous item
    const newOrder = [...group.tags];
    [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];

    // Get ordered array of tag IDs
    const orderedTagIds = newOrder.map(tag => tag.id);

    try {
      await tagService.reorderTags(groupId, orderedTagIds, teamId);

      // Update local state immediately for responsive UI
      setTagGroups(prev => prev.map(g =>
        g.id === groupId
          ? {
              ...g,
              tags: newOrder.map((tag, index) => ({
                ...tag,
                order_in_group: index
              }))
            }
          : g
      ));

      setSnackbar({
        open: true,
        message: 'Tag moved up',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error moving tag up:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder tag',
        severity: 'error',
      });
    }
  };

  const handleMoveTagDown = async (groupId: string, tagId: string, currentIndex: number) => {
    const group = tagGroups.find(g => g.id === groupId);
    if (!group || !group.tags) return;
    if (currentIndex === group.tags.length - 1) return; // Already at bottom

    // Create new order by swapping with next item
    const newOrder = [...group.tags];
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];

    // Get ordered array of tag IDs
    const orderedTagIds = newOrder.map(tag => tag.id);

    try {
      await tagService.reorderTags(groupId, orderedTagIds, teamId);

      // Update local state immediately for responsive UI
      setTagGroups(prev => prev.map(g =>
        g.id === groupId
          ? {
              ...g,
              tags: newOrder.map((tag, index) => ({
                ...tag,
                order_in_group: index
              }))
            }
          : g
      ));

      setSnackbar({
        open: true,
        message: 'Tag moved down',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error moving tag down:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder tag',
        severity: 'error',
      });
    }
  };

  // Drag and drop handlers (kept for moving between groups)
  const handleDragStart = (e: React.DragEvent, data: DragData) => {
    setDraggedItem(data);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetGroupId: string | null, targetPosition?: number) => {
    e.preventDefault();

    if (!draggedItem) return;

    // For ordered groups, use up/down buttons instead of drag-and-drop reordering
    // Only allow drag-and-drop for moving tags between groups
    if (draggedItem.type === 'tag' && draggedItem.sourceGroupId === targetGroupId) {
      // Dropping in same group - ignore (use up/down buttons for reordering)
      setDraggedItem(null);
      setDropTarget(null);
      setDropPosition(null);
      return;
    }

    try {
      if (draggedItem.type === 'tag') {
        await moveTag(draggedItem.id, draggedItem.sourceGroupId, targetGroupId);
        
        const sourceText = draggedItem.sourceGroupId 
          ? tagGroups.find(g => g.id === draggedItem.sourceGroupId)?.name || 'group'
          : 'individual tags';
        const targetText = targetGroupId 
          ? tagGroups.find(g => g.id === targetGroupId)?.name || 'group'
          : 'individual tags';
        
        setSnackbar({
          open: true,
          message: `Tag moved from ${sourceText} to ${targetText}`,
          severity: 'success',
        });
      }
      // Handle group reordering if needed in the future
      
    } catch (error) {
      console.error('Error in handleDrop:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to move tag';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }

    setDraggedItem(null);
    setDropTarget(null);
    setDropPosition(null);
  };

  const moveTag = async (tagId: string, sourceGroupId: string | undefined, targetGroupId: string | null) => {
    try {
      if (targetGroupId === null) {
        // Moving to solo - use moveTagToSolo API
        console.log('⬅️ Moving tag to solo:', tagId, 'with context:', { organizationId, teamId });
        await tagService.moveTagToSolo(tagId, {
          organization_id: organizationId,
          team_id: teamId
        });
        console.log('🔄 Refreshing after move to solo...');
      } else {
        // Moving to a group - use moveTagToGroup API
        console.log('➡️ Moving tag to group:', tagId, '→', targetGroupId, 'with context:', { organizationId, teamId });
        await tagService.moveTagToGroup(tagId, {
          tag_group_id: targetGroupId,
          organization_id: organizationId,
          team_id: teamId
        });
        console.log('🔄 Refreshing after move to group...');
      }

      // Instead of manually updating state, refresh from server to ensure consistency
      await handleSoftRefresh();

    } catch (error) {
      console.error('Error moving tag:', error);
      throw error; // Re-throw to be caught by handleDrop
    }
  };

  const handleReorderTags = async (groupId: string, draggedTagId: string, targetPosition: number) => {
    try {
      const group = tagGroups.find(g => g.id === groupId);
      if (!group || !group.tags) return;

      // Create new order by inserting the dragged item at the target position
      const filteredTags = group.tags.filter(tag => tag.id !== draggedTagId);
      const draggedTag = group.tags.find(tag => tag.id === draggedTagId);
      if (!draggedTag) return;

      // Insert at target position
      const newOrder = [...filteredTags];
      newOrder.splice(targetPosition, 0, draggedTag);

      // Get ordered array of tag IDs
      const orderedTagIds = newOrder.map(tag => tag.id);

      // Call API to reorder tags
      await tagService.reorderTags(groupId, orderedTagIds, teamId);

      // Update local state with new order
      setTagGroups(prev => prev.map(g => 
        g.id === groupId 
          ? {
              ...g,
              tags: newOrder.map((tag, index) => ({
                ...tag,
                order_in_group: index
              }))
            }
          : g
      ));

      setSnackbar({
        open: true,
        message: 'Tags reordered successfully',
        severity: 'success',
      });

    } catch (error) {
      console.error('Error reordering tags:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder tags',
        severity: 'error',
      });
    }
  };

  // CRUD operations
  const handleCreateTagGroup = async () => {
    if (!newGroupName.trim()) return;
    
    if (!organizationId) {
      console.error('TagManager: Cannot create tag group - organizationId is not available');
      setSnackbar({
        open: true,
        message: 'Cannot create tag group - organization not loaded',
        severity: 'error',
      });
      return;
    }

    try {
      console.log('TagManager: Creating tag group with data:', {
        organizationId,
        teamId,
        newGroupName,
        newGroupDescription,
        newGroupIsOrdered,
        newGroupAllowMultiple,
        newGroupColor
      });

      const createdGroupData = {
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || '',
        organization_id: organizationId,
        team_id: teamId,
        is_ordered: newGroupIsOrdered,
        allow_multiple: newGroupAllowMultiple,
        color_scheme: newGroupColor,
        tags: []
      };
      
      console.log('TagManager: Creating tag group with :', createdGroupData);
      
      // Use proper API call - let errors surface
      console.log('🆕 Creating tag group:', newGroupName);
      await tagService.createTagGroup(createdGroupData);
      
      // Reset form
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupIsOrdered(false);
      setNewGroupAllowMultiple(true);
      setNewGroupColor(randomTagColor());
      setShowCreateGroupDialog(false);

      setSnackbar({
        open: true,
        message: 'Tag group created successfully',
        severity: 'success',
      });
      
      console.log('🔄 Refreshing after tag group creation...');
      await handleSoftRefresh();
    } catch (error) {
      console.error('TagManager: Error creating tag group:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create tag group',
        severity: 'error',
      });
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      // Create unified tag using the new API
      const tagData: CreateTagRequest = {
        name: newTagName.trim(),
        description: newTagDescription.trim() || '',
        color: newTagColor,
        scope: 'team' as TagScope,
        organization_id: organizationId,
        team_id: teamId,
        tag_group_id: newTagTargetGroupId === 'solo' ? undefined : newTagTargetGroupId,
        owner_id: teamId
      };
      
      console.log('🆕 Creating tag:', tagData.name);
      await tagService.createUnifiedTag(tagData);
      
      // Reset form
      setNewTagName('');
      setNewTagDescription('');
      setNewTagColor(randomTagColor());
      setNewTagTargetGroupId('solo');
      setShowCreateTagDialog(false);

      setSnackbar({
        open: true,
        message: 'Tag created successfully',
        severity: 'success',
      });
      
      console.log('🔄 Refreshing after tag creation...');
      await handleSoftRefresh();
    } catch (error) {
      console.error('Error creating unified tag:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create tag',
        severity: 'error',
      });
    }
  };

  const handleDeleteTag = async (tagId: string, groupId?: string) => {
    try {
      console.log('🗑️ Deleting tag:', tagId, 'with context:', { organizationId, teamId });
      // Use unified tag deletion API with team context
      const result = await tagService.deleteUnifiedTag(tagId, organizationId, teamId);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Tag deleted successfully',
          severity: 'success',
        });
        
        console.log('🔄 Refreshing after tag deletion...');
        await handleSoftRefresh();
      } else {
        throw new Error(result.message || 'Failed to delete tag');
      }
    } catch (error) {
      console.error('Error deleting unified tag:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete tag';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDeleteTagGroup = async (groupId: string) => {
    try {
      console.log('🗑️ Deleting tag group:', groupId, 'with context:', { organizationId, teamId });
      // Use proper API call - let errors surface
      const result = await tagService.deleteTagGroup(groupId, organizationId, teamId);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Tag group deleted successfully',
          severity: 'success',
        });
        
        console.log('🔄 Refreshing after tag group deletion...');
        await handleSoftRefresh();
      } else {
        throw new Error(result.message || 'Failed to delete tag group');
      }
    } catch (error) {
      console.error('Error deleting tag group:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete tag group';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleEditTag = (tag: any, groupId?: string) => {
    setEditingItem({ 
      type: 'tag', 
      item: { ...tag, groupId } 
    });
    setShowEditDialog(true);
  };

  const handleSaveTagEdit = async () => {
    if (!editingItem || editingItem.type !== 'tag') return;
    
    const { item } = editingItem;
    
    try {
      // Use unified tag update API with team context
      const updateData: UpdateTagRequest = {
        name: item.name,
        description: item.description || '',
        color: item.color,
        organization_id: item.organization_id || organizationId,
        team_id: item.team_id || teamId
      };

      console.log('✏️ Updating tag:', item.id, 'with context:', { organizationId, teamId });
      await tagService.updateUnifiedTag(item.id, updateData);
      
      setEditingItem(null);
      setShowEditDialog(false);
      
      setSnackbar({
        open: true,
        message: 'Tag updated successfully',
        severity: 'success',
      });
      
      console.log('🔄 Refreshing after tag update...');
      await handleSoftRefresh();
    } catch (error) {
      console.error('Error updating unified tag:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update tag';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleSaveGroupEdit = async () => {
    if (!editingItem || editingItem.type !== 'group') return;

    const { item } = editingItem;

    try {
      // Make real API call to update tag group
      const updateData = {
        name: item.name,
        description: item.description || '',
        is_ordered: item.is_ordered || false,
        allow_multiple: item.allow_multiple !== false, // Default to true
        color_scheme: item.color_scheme || '#2196f3',
        organization_id: item.organization_id || organizationId,
        team_id: item.team_id || teamId
      };

      console.log('✏️ Updating tag group:', item.id, 'with context:', { organizationId, teamId });
      await tagService.updateTagGroup(item.id, updateData);
      
      // Reset the editing item so the modal reopens with fresh values
      setEditingItem(null);
      setShowEditDialog(false);
      
      setSnackbar({
        open: true,
        message: 'Tag group updated successfully',
        severity: 'success',
      });
      
      console.log('🔄 Refreshing after tag group update...');
      await handleSoftRefresh();
    } catch (error) {
      console.error('Error updating tag group:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update tag group';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  // Color picker component
  const ColorPicker: React.FC<{ 
    selectedColor: string; 
    onColorChange: (color: string) => void;
    label: string;
  }> = ({ selectedColor, onColorChange, label }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" gutterBottom>{label}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 280 }}>
        {colorPalette.map((color) => (
          <Box
            key={color}
            onClick={() => onColorChange(color)}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: color,
              border: selectedColor === color ? '3px solid #000' : '1px solid #ddd',
              borderRadius: '50%',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );

  // Get tag display colors (tag color as background fill with opacity, group color as border outline, black text)
  const getTagColors = (tag: any, group?: TagGroup) => {
    if (group) {
      return {
        backgroundColor: (tag.color || '#ffffff') + '30', // Tag color as background fill with transparency
        borderColor: group.color_scheme, // Group color as border outline
        color: '#000000', // Black text
      };
    }
    return {
      backgroundColor: (tag.color || '#2196f3') + '20',
      borderColor: tag.color || '#2196f3',
      color: '#000000', // Black text for solo tags too
    };
  };

  // Track which accordions are expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Soft refresh that preserves UI state
  const handleSoftRefresh = async () => {
    console.log('🔄 Soft refresh triggered - preserving UI state');
    try {
      console.log('📡 Fetching fresh data...');
      
      // Fetch fresh data
      const [tagGroupsResponse, allTagsResponse] = await Promise.all([
        tagService.getTagGroupsWithTags({ organization_id: organizationId, team_id: teamId }),
        tagService.listUnifiedTags({
          scope: 'team',  // Use correct scope parameter (matches TagScope.TEAM proto enum)
          organization_id: organizationId,
          team_id: teamId,
          limit: 1000,  // Increased limit to show all tags
          sort_by: 'created',  // Sort by creation date
          sort_order: 'desc'  // Newest first
        })
      ]);
      
      console.log('🔄 Soft refresh: Tag groups response:', tagGroupsResponse);
      console.log('🔄 Soft refresh: All tags response:', allTagsResponse);
      
      // The service now properly extracts the data, returning arrays directly
      const groupsData = Array.isArray(tagGroupsResponse) ? tagGroupsResponse : [];
      const allTagsData = Array.isArray(allTagsResponse) ? allTagsResponse : [];
      console.log('🔄 Soft refresh: Processing', groupsData.length, 'tag groups from API');
      
      const processedGroups = groupsData.map(groupData => {
        const group = groupData;

        // Map both camelCase and snake_case fields from API for maximum compatibility
        const rawIsOrdered = group.is_ordered ?? (group as any).isOrdered ?? false;
        const rawAllowMultiple = group.allow_multiple ?? (group as any).allowMultiple ?? (group as any).allows_multiple ?? true;
        const rawColorScheme = group.color_scheme ?? (group as any).colorScheme ?? group.color ?? '#666666';

        console.log(`🔄 Soft refresh: Normalizing group "${group.name}":`, {
          raw_is_ordered: group.is_ordered,
          raw_isOrdered: (group as any).isOrdered,
          normalized_is_ordered: rawIsOrdered
        });

        // Map camelCase fields from API to snake_case fields expected by component
        const normalizedGroup = {
          ...group,
          is_ordered: rawIsOrdered,
          allow_multiple: rawAllowMultiple,
          color_scheme: rawColorScheme,
          organization_id: group.organization_id,
          team_id: group.team_id,
          created_at: group.created_at,
          updated_at: group.updated_at
        };
        
        const tags = (groupData.tags || []).map(unifiedTag => ({
          id: unifiedTag.id,
          name: unifiedTag.name,
          description: unifiedTag.description || '',
          color: unifiedTag.color || '#666666',
          order_in_group: unifiedTag.order ?? 0,
          tag_group_id: unifiedTag.tag_group_id
        }));

        let sortedTags = tags;
        if (normalizedGroup.is_ordered) {
          sortedTags = [...tags].sort((a, b) => (a.order_in_group || 0) - (b.order_in_group || 0));
        } else {
          sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name));
        }

        console.log(`🔄 Soft refresh: Processed group "${group.name}" with ${sortedTags.length} tags`);

        return {
          ...normalizedGroup,
          tags: sortedTags
        };
      });
      
      setTagGroups(processedGroups);
      
      // Process solo tags - keep them as UnifiedTag type
      console.log('🔄 Soft refresh: All tags from API:', allTagsData.length, 'tags');
      const groupedTagIds = new Set(
        processedGroups.flatMap(g => g.tags.map(t => t.id))
      );
      console.log('🔄 Soft refresh: Tag IDs in groups:', Array.from(groupedTagIds));
      
      // Filter solo tags to only include team/organizational tags not in groups
      const soloTagsList = allTagsData.filter(tag => {
        const isNotInGroup = !groupedTagIds.has(tag.id);
        // Check if it's a team tag (not personal) and is marked as solo
        const hasTeamScope = tag.scope === 'team';
        const belongsToTeam = tag.team_id === teamId || (tag as any).owner_id === teamId;
        const isSolo = tag.is_solo === true;
        
        console.log(`🔄 Soft refresh: Filtering tag "${tag.name}":`, {
          isNotInGroup,
          hasTeamScope,
          belongsToTeam,
          isSolo,
          scope: tag.scope,
          team_id: tag.team_id,
          owner_id: (tag as any).owner_id,
          is_solo: tag.is_solo
        });
        
        // Solo tags should be team tags that are not in groups
        // For now, ignore is_solo flag and owner_id check since API might not be setting them correctly  
        return isNotInGroup && hasTeamScope;
      });
      console.log('🔄 Soft refresh: Found', soloTagsList.length, 'solo team tags after filtering');
      
      setSoloTags(soloTagsList);
      
      console.log('📝 State updated - soft refresh complete');
    } catch (error) {
      console.error('❌ Failed to refresh tags:', error);
    }
  };
  
  // Manual refresh handler (for button) - use soft refresh
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered');
    await handleSoftRefresh();
    setSnackbar({
      open: true,
      message: 'Tags refreshed',
      severity: 'success',
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading tag manager...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Action toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateTagDialog(true)}
        >
          Add Tag
        </Button>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => setShowCreateGroupDialog(true)}
        >
          Create Group
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Tag Groups */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon /> Tag Groups
            </Typography>

            {tagGroups.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No tag groups created yet. Create a group to organize related tags.
              </Alert>
            ) : (
              tagGroups.map((group) => (
                <Accordion 
                  key={group.id} 
                  sx={{ mb: 1 }}
                  expanded={expandedGroups.has(group.id)}
                  onChange={(_, isExpanded) => {
                    setExpandedGroups(prev => {
                      const newSet = new Set(prev);
                      if (isExpanded) {
                        newSet.add(group.id);
                      } else {
                        newSet.delete(group.id);
                      }
                      return newSet;
                    });
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIcon sx={{ color: 'text.disabled' }} />
                        <Typography variant="h6">{group.name}</Typography>
                        <Chip 
                          size="small" 
                          label={group.tags?.length || 0} 
                          color="primary" 
                        />
                        {group.is_ordered && (
                          <Tooltip title="This group has ordered tags. You can drag and drop tags to reorder them.">
                            <Chip 
                              size="small" 
                              label="Ordered" 
                              variant="outlined" 
                              icon={<ReorderIcon />}
                              color="secondary"
                            />
                          </Tooltip>
                        )}
                        {!group.allow_multiple && (
                          <Chip size="small" label="Exclusive" variant="outlined" />
                        )}
                      </Box>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Map API properties to form state format
                            // Check both is_ordered and isOrdered for compatibility
                            const rawIsOrdered = group.is_ordered ?? (group as any).isOrdered ?? false;
                            const rawAllowMultiple = group.allow_multiple ?? (group as any).allowMultiple ?? (group as any).allows_multiple ?? true;

                            const groupForEditing = {
                              ...group,
                              is_ordered: rawIsOrdered,
                              allow_multiple: rawAllowMultiple,
                              color_scheme: group.color_scheme || '#2196f3'
                            };
                            console.log('Opening edit modal for group:', group.id, 'with values:', {
                              raw_group_data: group,
                              is_ordered_from_api: group.is_ordered,
                              isOrdered_from_api: (group as any).isOrdered,
                              allow_multiple_from_api: group.allow_multiple,
                              allowMultiple_from_api: (group as any).allowMultiple,
                              allows_multiple_from_api: (group as any).allows_multiple,
                              mapped_is_ordered: groupForEditing.is_ordered,
                              mapped_allow_multiple: groupForEditing.allow_multiple
                            });
                            setEditingItem({ type: 'group', item: groupForEditing });
                            setShowEditDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                        {group.description}
                      </Typography>
                    )}
                    
                    <Box
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, group.id)}
                      sx={{
                        minHeight: 60,
                        border: '2px dashed',
                        borderColor: dropTarget === group.id ? 'primary.main' : 'transparent',
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: dropTarget === group.id ? 'primary.light' : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                      onDragEnter={() => setDropTarget(group.id)}
                      onDragLeave={() => setDropTarget(null)}
                    >
                      {group.is_ordered ? (
                        // Ordered group - render vertically with up/down buttons
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {(!group.tags || group.tags.length === 0) ? (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              No tags in this ordered group. Create tags or drag them here.
                            </Typography>
                          ) : (
                            group.tags
                              .sort((a, b) => (a.order_in_group || 0) - (b.order_in_group || 0))
                              .map((tag, index) => (
                                <Box
                                  key={tag.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    width: '100%'
                                  }}
                                >
                                  {/* Reorder buttons */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveTagUp(group.id, tag.id, index)}
                                      disabled={index === 0}
                                      sx={{
                                        padding: 0,
                                        minWidth: 0,
                                        width: 24,
                                        height: 16,
                                        '&:hover': {
                                          backgroundColor: 'action.hover'
                                        }
                                      }}
                                    >
                                      <ArrowUpIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveTagDown(group.id, tag.id, index)}
                                      disabled={index === group.tags!.length - 1}
                                      sx={{
                                        padding: 0,
                                        minWidth: 0,
                                        width: 24,
                                        height: 16,
                                        '&:hover': {
                                          backgroundColor: 'action.hover'
                                        }
                                      }}
                                    >
                                      <ArrowDownIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>

                                  {/* Position number and tag */}
                                  <Chip
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.7rem',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>
                                        <Typography sx={{ flexGrow: 1 }}>{tag.name}</Typography>
                                      </Box>
                                    }
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, {
                                      type: 'tag',
                                      id: tag.id,
                                      sourceGroupId: group.id,
                                      originalIndex: index
                                    })}
                                    sx={{
                                      flex: 1,
                                      justifyContent: 'space-between',
                                      cursor: 'grab',
                                      '&:active': { cursor: 'grabbing' },
                                      ...getTagColors(tag, group),
                                    }}
                                    variant="outlined"
                                    deleteIcon={
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {tag.description && (
                                          <Tooltip title={tag.description}>
                                            <InfoIcon fontSize="small" />
                                          </Tooltip>
                                        )}
                                        <EditIcon fontSize="small" />
                                      </Box>
                                    }
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      handleEditTag(tag, group.id);
                                    }}
                                  />
                                </Box>
                              ))
                          )}
                        </Box>
                      ) : (
                        // Unordered group - original behavior
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(!group.tags || group.tags.length === 0) ? (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              No tags in this group. Drag tags here or create new ones.
                            </Typography>
                          ) : (
                            group.tags
                              .sort((a, b) => (a.order_in_group || 0) - (b.order_in_group || 0))
                              .map((tag, index) => (
                                <Chip
                                  key={tag.id}
                                  label={tag.name}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, {
                                    type: 'tag',
                                    id: tag.id,
                                    sourceGroupId: group.id,
                                    originalIndex: index
                                  })}
                                  sx={{
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                    ...getTagColors(tag, group),
                                  }}
                                  variant="outlined"
                                  deleteIcon={
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {tag.description && (
                                        <Tooltip title={tag.description}>
                                          <InfoIcon fontSize="small" />
                                        </Tooltip>
                                      )}
                                      <EditIcon fontSize="small" />
                                    </Box>
                                  }
                                  onDelete={(e) => {
                                    e.stopPropagation();
                                    handleEditTag(tag, group.id);
                                  }}
                                />
                              ))
                          )}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Paper>
        </Grid>

        {/* Solo Tags */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TagIcon /> Individual Tags
            </Typography>
            
            <Box
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, null)}
              sx={{
                minHeight: 100,
                border: '2px dashed',
                borderColor: dropTarget === 'solo' ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                p: 2,
                backgroundColor: dropTarget === 'solo' ? 'primary.light' : 'grey.50',
                transition: 'all 0.2s ease',
              }}
              onDragEnter={() => setDropTarget('solo')}
              onDragLeave={() => setDropTarget(null)}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {soloTags.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                    No individual tags. Create tags or drag them here from groups.
                  </Typography>
                ) : (
                  soloTags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      draggable
                      onDragStart={(e) => handleDragStart(e, {
                        type: 'tag',
                        id: tag.id,
                        sourceGroupId: undefined
                      })}
                      sx={{
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        ...getTagColors(tag),
                        justifyContent: 'space-between',
                      }}
                      variant="outlined"
                      deleteIcon={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {tag.description && (
                            <Tooltip title={tag.description}>
                              <InfoIcon fontSize="small" />
                            </Tooltip>
                          )}
                          <EditIcon fontSize="small" />
                        </Box>
                      }
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleEditTag(tag);
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Create Tag Group Dialog */}
      <Dialog open={showCreateGroupDialog} onClose={() => setShowCreateGroupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Tag Group</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description (Optional)"
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Help AI understand when to suggest tags from this group..."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newGroupIsOrdered}
                  onChange={(e) => setNewGroupIsOrdered(e.target.checked)}
                />
              }
              label="Ordered tags (e.g., funnel stages)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newGroupAllowMultiple}
                  onChange={(e) => setNewGroupAllowMultiple(e.target.checked)}
                />
              }
              label="Allow multiple tag selection"
            />
            <ColorPicker
              selectedColor={newGroupColor}
              onColorChange={setNewGroupColor}
              label="Group Color"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateGroupDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateTagGroup} variant="contained" disabled={!newGroupName.trim()}>
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Tag Dialog */}
      <Dialog open={showCreateTagDialog} onClose={() => setShowCreateTagDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Tag</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Tag Name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description (Optional)"
              value={newTagDescription}
              onChange={(e) => setNewTagDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Help AI understand when to suggest this tag..."
            />
            <FormControl fullWidth>
              <InputLabel>Add to Group</InputLabel>
              <Select
                value={newTagTargetGroupId}
                onChange={(e) => setNewTagTargetGroupId(e.target.value)}
                label="Add to Group"
              >
                <MenuItem value="solo">Individual Tag (No Group)</MenuItem>
                {tagGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ColorPicker
              selectedColor={newTagColor}
              onColorChange={setNewTagColor}
              label="Tag Color"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateTagDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateTag} variant="contained" disabled={!newTagName.trim()}>
            Create Tag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog (for both tags and groups) */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {editingItem?.type === 'tag' ? 'Tag' : 'Tag Group'}
        </DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={editingItem.type === 'tag' ? 'Tag Name' : 'Group Name'}
                value={editingItem.item.name || ''}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  item: { ...editingItem.item, name: e.target.value }
                })}
                fullWidth
                required
              />
              <TextField
                label="Description (Optional)"
                value={editingItem.item.description || ''}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  item: { ...editingItem.item, description: e.target.value }
                })}
                fullWidth
                multiline
                rows={2}
                placeholder={editingItem.type === 'tag' 
                  ? "Help AI understand when to suggest this tag..."
                  : "Help AI understand when to suggest tags from this group..."
                }
              />
              
              {editingItem.type === 'group' ? (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingItem.item.is_ordered || false}
                        onChange={(e) => setEditingItem({
                          ...editingItem,
                          item: { ...editingItem.item, is_ordered: e.target.checked }
                        })}
                      />
                    }
                    label="Ordered tags (e.g., funnel stages)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingItem.item.allow_multiple !== false}
                        onChange={(e) => setEditingItem({
                          ...editingItem,
                          item: { ...editingItem.item, allow_multiple: e.target.checked }
                        })}
                      />
                    }
                    label="Allow multiple tag selection"
                  />
                  <ColorPicker
                    selectedColor={editingItem.item.color_scheme || '#2196f3'}
                    onColorChange={(color) => setEditingItem({
                      ...editingItem,
                      item: { ...editingItem.item, color_scheme: color }
                    })}
                    label="Group Color"
                  />
                </>
              ) : (
                <ColorPicker
                  selectedColor={editingItem.item.color || '#ffffff'}
                  onColorChange={(color) => setEditingItem({
                    ...editingItem,
                    item: { ...editingItem.item, color }
                  })}
                  label="Tag Color"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {editingItem?.type === 'tag' && (
              <Button
                onClick={() => {
                  if (editingItem) {
                    setConfirmDialog({
                      open: true,
                      title: 'Delete Tag',
                      content: `Are you sure you want to delete the tag "${editingItem.item.name}"?`,
                      severity: 'warning',
                      onConfirm: () => {
                        handleDeleteTag(editingItem.item.id, editingItem.item.groupId);
                        setShowEditDialog(false);
                        setEditingItem(null);
                      },
                    });
                  }
                }}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete Tag
              </Button>
            )}
            {editingItem?.type === 'group' && (
              <Button
                onClick={() => {
                  if (editingItem) {
                    const group = tagGroups.find(g => g.id === editingItem.item.id);
                    const tagCount = group?.tags?.length || 0;
                    const content = tagCount > 0
                      ? `This group contains ${tagCount} tag(s). Deleting the group will also delete all its tags. Are you sure you want to continue?`
                      : `Are you sure you want to delete the tag group "${editingItem.item.name}"?`;
                    setConfirmDialog({
                      open: true,
                      title: 'Delete Tag Group',
                      content,
                      severity: 'error',
                      onConfirm: () => {
                        handleDeleteTagGroup(editingItem.item.id);
                        setShowEditDialog(false);
                        setEditingItem(null);
                      },
                    });
                  }
                }}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete Group
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={editingItem?.type === 'tag' ? handleSaveTagEdit : handleSaveGroupEdit}
              variant="contained" 
              disabled={!editingItem?.item.name?.trim()}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmText="Delete"
        severity={confirmDialog.severity}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default TagManager;

